import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import MD5 from "crypto-js/md5";
import dayjs from "dayjs";
import {
    getContractListThunk,
    getContractTypesListThunk,
    getDebtListByPointIdThunk,
    getFullUnassignedPlanByRouteIdThunk,
    getKpiGetPointProgressThunk,
    getMotivationInfoThunk,
    getPlanGetByPointIdThunk,
    getPointInventoryGetThunk,
    getPriceListByPointThunk,
    getStorageInventoryGetThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
    PostOrderSaveParams,
} from "tm-shared/api";
import { Price } from "tm-shared/types";
import { arrayToRecord, uniqueByKey } from "tm-shared/utils";
import { normalizeNestedObjectMap, normalizeObjectMap } from "tm-shared/utils";

import { QueueProgress, QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { makeCommand } from "@/hooks/requestQueue/useRequestQueue";
import { RequestQueueItem } from "@/types/requestQueue";

import {
    getKpiGetPointProgressesThunk,
    getPlansByPointIdThunk,
} from "./offline.slice.thunks";
import { OfflineState } from "./offline.slice.types";

function getInitialState(): OfflineState {
    return {
        pointsPriceLists: {},
        pointsInventories: {},
        isUnscheduledPointsProcessing: false,

        offlineMode: false,
        manualOfflineMode: false,
        showOnlinePopup: false,
        debts: {},
        priceLists: {},
        inventoryList: {},
        targetsContracts: {},
        promoList: {},
        promosVocabulary: {},
        ignoreRemainingsOrders: {},
        filteredParams: [],
        contractTypesList: {},
        plans: {},
        facts: {},
        motivationInfoDate: "",
        latency: 0,
        lastOfflineUpdate: null,
        shouldUpdateKpi: false,
        isInitialDataLoading: true,

        requestQueueStatus: {
            isRunning: false,
            lastTickAt: null,
        },
        requestQueue: [],

        photoUploadRetry: {
            nextPhotoUploadRetryAt: null,
            candidatesCount: 0,
            ignoreBackoffOnce: false,
            progress: {
                tasks: [],
                isRunning: false,
                completedCount: 0,
                totalCount: 0,
            },
        },

        progress: {
            lastModifiedAt: "",
            lastSyncedAt: "",
        },

        reminderTimestamps: {
            checkout: "",
            task: "",
        },
    };
}

const initialState: OfflineState = getInitialState();

const applyPendingReplacements = <T>(
    value: T,
    replacements?: Record<string, string>,
): T => {
    if (!replacements || Object.keys(replacements).length === 0) {
        return value;
    }

    let raw = JSON.stringify(value);

    for (const [replaceValue, actualValue] of Object.entries(replacements)) {
        raw = raw.replaceAll(replaceValue, actualValue);
    }

    return JSON.parse(raw) as T;
};

const bumpCommandsRevision = (currentRevision: number | undefined): number =>
    (currentRevision ?? 0) + 1;

export const offlineSlice = createSlice({
    name: "offline",
    initialState,
    reducers: {
        updateOfflineStatus(state, action: PayloadAction<boolean>) {
            state.offlineMode = action.payload;
        },
        updateManualOfflineStatus(state, action) {
            state.manualOfflineMode = action.payload;
        },

        setLatency(state, action: PayloadAction<number | "offline">) {
            state.latency = action.payload;
        },
        setShowOnlinePopup(state, action: PayloadAction<boolean>) {
            state.showOnlinePopup = action.payload;
        },

        addIgnoreRemainingOrder(
            state,
            action: PayloadAction<{ orderId: string; barcode: string }>,
        ) {
            const { orderId, barcode } = action.payload;

            const existingBarcodes = state.ignoreRemainingsOrders[orderId];

            if (!existingBarcodes) {
                state.ignoreRemainingsOrders[orderId] = [barcode];
            } else if (!existingBarcodes.includes(barcode)) {
                existingBarcodes.push(barcode);
            }
        },

        removeIgnoreRemainingOrders(state, action: PayloadAction<string[]>) {
            action.payload.forEach((orderId) => {
                delete state.ignoreRemainingsOrders[orderId];
            });
        },

        setLastOfflineUpdate(state, action: PayloadAction<string>) {
            state.lastOfflineUpdate = action.payload;
        },

        setShouldUpdateKpi(state, action: PayloadAction<boolean>) {
            state.shouldUpdateKpi = action.payload;
        },
        setUpdateUnscheduledPointsProcessing(
            state,
            action: PayloadAction<boolean>,
        ) {
            state.isUnscheduledPointsProcessing = action.payload;
        },
        setIsInitialDataLoading(state, action: PayloadAction<boolean>) {
            state.isInitialDataLoading = action.payload;
        },

        // -------------------- Request Queue -------------------

        setRequestQueueRunning(state, action: PayloadAction<boolean>) {
            const status = action.payload;

            state.requestQueueStatus.isRunning = status;

            if (status) {
                state.requestQueueStatus.lastTickAt = Date.now();
            } else {
                state.requestQueueStatus.lastTickAt = null;
            }
        },

        touchRequestQueue(state) {
            state.requestQueueStatus.lastTickAt = Date.now();
        },

        bulkAddRequestQueueItems(
            state,
            action: PayloadAction<RequestQueueItem[]>,
        ) {
            const normalizeQueue = uniqueByKey(
                [...state.requestQueue, ...action.payload],
                "id",
            );

            state.requestQueue = normalizeQueue;
        },

        addRequestQueueItem: (
            state,
            action: PayloadAction<
                Omit<
                    RequestQueueItem,
                    "status" | "createdAt" | "commandsRevision"
                >
            >,
        ) => {
            const incoming = action.payload;

            const requestQueueItem = state.requestQueue.find(
                (q) => q.id === incoming.id,
            );

            if (requestQueueItem) {
                const normalizedCommands = applyPendingReplacements(
                    incoming.commands,
                    requestQueueItem.pendingReplacements,
                );

                const normalizedMetadata = applyPendingReplacements(
                    incoming.metadata,
                    requestQueueItem.pendingReplacements,
                );

                requestQueueItem.commands.push(...normalizedCommands);
                requestQueueItem.metadata = {
                    ...requestQueueItem.metadata,
                    ...normalizedMetadata,
                };
                requestQueueItem.options = {
                    ...requestQueueItem?.options,
                    ...incoming?.options,
                };
                requestQueueItem.status = QueueTaskStatus.Pending;

                requestQueueItem.commandsRevision = bumpCommandsRevision(
                    requestQueueItem.commandsRevision,
                );
            } else {
                state.requestQueue.push({
                    ...incoming,
                    status: QueueTaskStatus.Pending,
                    createdAt: dayjs().toISOString(),
                    commandsRevision: 0,
                });
            }
        },

        addOrReplaceRequestQueueItem: (
            state,
            action: PayloadAction<
                Omit<
                    RequestQueueItem,
                    "status" | "createdAt" | "commandsRevision"
                >
            >,
        ) => {
            const newItem: RequestQueueItem = {
                ...action.payload,
                status: QueueTaskStatus.Pending,
                createdAt: dayjs().toISOString(),
                commandsRevision: 0,
            };

            const currentIndex = state.requestQueue.findIndex(
                (q) => q.id === newItem.id,
            );

            if (currentIndex !== -1) {
                state.requestQueue[currentIndex] = newItem;
            } else {
                state.requestQueue.push(newItem);
            }
        },

        upsertSaleOrderUpdateRequestQueueItem: (
            state,
            action: PayloadAction<{
                orderId: string;
                orderPayload: PostOrderSaveParams;
            }>,
        ) => {
            const { orderId, orderPayload } = action.payload;

            const requestQueueItem = state.requestQueue.find((item) => {
                if (item?.metadata?.orderId === orderId) {
                    return true;
                }

                return (
                    item?.pendingReplacements?.[orderId] ===
                    item?.metadata?.orderId
                );
            });

            if (!requestQueueItem) {
                state.requestQueue.push({
                    id: orderId,
                    metadata: {
                        orderId,
                        title: `Оновлення даних замовлення: ${orderId}`,
                    },
                    options: { todayOnly: true },
                    createdAt: dayjs().toISOString(),
                    status: QueueTaskStatus.Pending,
                    commands: [
                        makeCommand({
                            type: "postOrderSave",
                            title: `Збереження змін`,
                            params: orderPayload,
                        }),
                    ],
                    commandsRevision: 0,
                });

                return;
            }

            requestQueueItem.status = QueueTaskStatus.Pending;

            const postOrderSaveCmd = requestQueueItem.commands.find(
                (c) => c.type === "postOrderSave",
            );

            if (!postOrderSaveCmd) {
                requestQueueItem.commands.push(
                    makeCommand({
                        type: "postOrderSave",
                        title: `Збереження змін`,
                        params: applyPendingReplacements(
                            orderPayload,
                            requestQueueItem.pendingReplacements,
                        ),
                    }),
                );
                requestQueueItem.commandsRevision = bumpCommandsRevision(
                    requestQueueItem.commandsRevision,
                );

                return;
            }

            const normalizedParams = applyPendingReplacements(
                orderPayload,
                requestQueueItem.pendingReplacements,
            );

            postOrderSaveCmd.params = normalizedParams;
            requestQueueItem.commandsRevision = bumpCommandsRevision(
                requestQueueItem.commandsRevision,
            );
        },

        deleteRequestQueueItem: (state, action: PayloadAction<string>) => {
            state.requestQueue = state.requestQueue.filter(
                (i) => i.id !== action.payload,
            );
        },

        patchRequestQueueItem: (
            state,
            action: PayloadAction<{
                id: string;
                patch: Partial<Pick<RequestQueueItem, "status" | "progress">>;
            }>,
        ) => {
            const { id, patch } = action.payload;

            const requestQueueItem = state.requestQueue.find(
                (i) => i.id === id,
            );

            if (!requestQueueItem) return;

            Object.assign(requestQueueItem, patch);
        },

        applyRequestQueueItemReplacements: (
            state,
            action: PayloadAction<{
                id: string;
                replacements: Record<string, string>;
            }>,
        ) => {
            const { id, replacements } = action.payload;

            const requestQueueItem = state.requestQueue.find(
                (i) => i.id === id,
            );

            if (!requestQueueItem) return;

            requestQueueItem.commands = applyPendingReplacements(
                requestQueueItem.commands,
                replacements,
            );

            requestQueueItem.metadata = applyPendingReplacements(
                requestQueueItem.metadata,
                replacements,
            );

            requestQueueItem.pendingReplacements = {
                ...requestQueueItem.pendingReplacements,
                ...replacements,
            };
        },

        restartRequestQueue: (
            state,
            action: PayloadAction<string | undefined>,
        ) => {
            const id = action.payload;

            const restartItem = (item: RequestQueueItem) => {
                item.status = QueueTaskStatus.Pending;
            };

            if (id) {
                const found = state.requestQueue.find((x) => x.id === id);
                if (found) restartItem(found);
            } else {
                state.requestQueue.map(restartItem);
            }
        },

        stopRequestQueue: (state) => {
            state.requestQueue = state.requestQueue.map((item) => ({
                ...item,
                status: QueueTaskStatus.Idle,
            }));
        },

        clearRequestQueue: (state) => {
            state.requestQueue = [];
        },

        normalizeRequestQueueTail: (state) => {
            const queue = state.requestQueue;

            const TAIL_CHECKOUT_ID = "tail:updateCheckout";
            const TAIL_TASKS_ID = "tail:refreshTasks";

            const hasCommand = (types: string[]) =>
                queue.some((item) =>
                    item.commands.some((c) => types.includes(c.type)),
                );

            const needCheckoutTail = hasCommand([
                "postOrderSave",
                "postOrderSaveOne",
            ]);
            const needTasksTail = hasCommand([
                "postTaskAddSaleTask",
                "postAddDistributionPrices",
                "postTaskAddDebtTask",
                "postTaskAddRejectTask",
                "postTaskComplete",
            ]);

            const takeOrCreateTail = (
                id: string,
                create: () => RequestQueueItem,
            ) => {
                const idx = queue.findIndex((x) => x.id === id);
                if (idx === -1) return create();
                const [it] = queue.splice(idx, 1);
                return it;
            };

            if (!needCheckoutTail && !needTasksTail) return;

            const checkoutTail = needCheckoutTail
                ? takeOrCreateTail(TAIL_CHECKOUT_ID, () => ({
                      id: TAIL_CHECKOUT_ID,
                      status: QueueTaskStatus.Pending,
                      metadata: {
                          title: "Синхронізація даних (кошик)",
                      },
                      createdAt: dayjs().toISOString(),
                      options: {
                          todayOnly: true,
                      },
                      commands: [
                          makeCommand({
                              type: "updateCheckout",
                              title: "Завантаження даних кошику",
                              params: null,
                          }),
                      ],
                      commandsRevision: 0,
                  }))
                : null;

            const tasksTail = needTasksTail
                ? takeOrCreateTail(TAIL_TASKS_ID, () => ({
                      id: TAIL_TASKS_ID,
                      status: QueueTaskStatus.Pending,
                      metadata: {
                          title: "Оновлення списку задач",
                      },
                      createdAt: dayjs().toISOString(),
                      options: {
                          todayOnly: true,
                      },
                      commands: [
                          makeCommand({
                              type: "getTaskListsThunk",
                              title: "Завантаження актуального списку задач",
                              params: null,
                          }),
                      ],
                      commandsRevision: 0,
                  }))
                : null;

            if (checkoutTail) queue.push(checkoutTail);
            if (tasksTail) queue.push(tasksTail);
        },

        // -----------------------------------------------------

        setPointInventories: (
            state,
            action: PayloadAction<{
                pointId: string;
                rest: Record<string, number>;
            }>,
        ) => {
            const { pointId, rest } = action.payload;

            state.pointsInventories[pointId] = rest;
        },

        deductInventory: (
            state,
            action: PayloadAction<{
                storageId: string;
                newInventory: {
                    [barcode: string]: number;
                };
            }>,
        ) => {
            const { storageId, newInventory } = action.payload;
            const currentStorage = state?.inventoryList?.[storageId] ?? {};
            for (const [barcode, count] of Object.entries(newInventory)) {
                const currentInventory = currentStorage?.[barcode] ?? 0;
                const newCount = currentInventory - count;

                currentStorage[barcode] = Math.max(0, newCount);
            }
        },

        // ----------------------------------- Photo upload retry
        setNextPhotoUploadRetryAt: (
            state,
            action: PayloadAction<{ time: string; ignoreBackoff?: boolean }>,
        ) => {
            const { time, ignoreBackoff = false } = action.payload;
            state.photoUploadRetry.nextPhotoUploadRetryAt = time;
            state.photoUploadRetry.ignoreBackoffOnce = ignoreBackoff;
        },
        setPhotoUploadRetryProgress: (
            state,
            action: PayloadAction<QueueProgress>,
        ) => {
            state.photoUploadRetry.progress = action.payload;
        },
        setPhotoUploadRetryCandidatesCount: (
            state,
            action: PayloadAction<number>,
        ) => {
            state.photoUploadRetry.candidatesCount = action.payload;
        },

        // --------------------------------------- Progress sync
        setProgressLastSyncedAt(state, action: PayloadAction<string>) {
            state.progress.lastSyncedAt = action.payload;
        },

        setProgressLastModifiedAt(state, action: PayloadAction<string>) {
            state.progress.lastModifiedAt = action.payload;
        },

        // -------------------------------------- Reminder
        setCheckoutReminder(state, { payload }: PayloadAction<string>) {
            state.reminderTimestamps.checkout = payload;
        },
        setTaskReminder(state, { payload }: PayloadAction<string>) {
            state.reminderTimestamps.task = payload;
        },
        setFilteredParams(state, { payload }: PayloadAction<string[]>) {
            state.filteredParams = payload;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(getContractTypesListThunk.fulfilled, (state, action) => {
                state.contractTypesList = action.payload?.result;
            })
            .addCase(getContractListThunk.fulfilled, (state, action) => {
                const newContractsMap = action.payload?.result?.reduce(
                    (acc, contract) => {
                        const targetId = contract.targetId;

                        if (!acc[targetId]) {
                            acc[targetId] = [];
                        }

                        acc[targetId].push(contract);

                        return acc;
                    },
                    {} as Record<
                        string,
                        (typeof action.payload.result)[number][]
                    >,
                );

                Object.entries(newContractsMap).forEach(
                    ([targetId, contracts]) => {
                        state.targetsContracts[targetId] = contracts;
                    },
                );
            })
            .addCase(
                getStorageInventoryGetThunk.fulfilled,
                (state, { payload, meta }) => {
                    state.inventoryList[meta.arg.storageId] =
                        normalizeObjectMap(payload.result);
                },
            )
            .addCase(
                getPointInventoryGetThunk.fulfilled,
                (state, { payload }) => {
                    const { pointId, rest } = payload.result;

                    state.pointsInventories[pointId] = normalizeObjectMap(rest);
                },
            )
            .addCase(getPlansByPointIdThunk.fulfilled, (state, { payload }) => {
                state.plans = {
                    ...payload,
                };
            })
            .addCase(
                getPlanGetByPointIdThunk.fulfilled,
                (state, { payload }) => {
                    if (!payload.result) return;
                    state.plans = {
                        ...state.plans,
                        [payload.result.targetId]:
                            payload.result.settings.items,
                    };
                },
            )

            .addCase(
                getKpiGetPointProgressesThunk.fulfilled,
                (state, { payload }) => {
                    state.facts = {
                        ...payload,
                    };
                },
            )
            .addCase(
                getKpiGetPointProgressThunk.fulfilled,
                (state, { payload, meta }) => {
                    if (!payload.result) return;
                    state.facts = {
                        ...state.facts,
                        [meta.arg.pointId]: arrayToRecord(
                            payload.result,
                            "barcode",
                        ),
                    };
                },
            )

            .addCase(
                getFullUnassignedPlanByRouteIdThunk.fulfilled,
                (state, { payload }) => {
                    if (!payload.result) return;
                    state.plans = {
                        ...state.plans,
                        unassignedPlan: payload.result.settings.items,
                    };
                },
            )
            .addCase(getMotivationInfoThunk.fulfilled, (state, { payload }) => {
                const result = payload?.result?.state.groups;

                if (!result) return;
                state.motivationInfoDate = payload?.result?.startDate;
            })
            .addCase(
                getPriceListByPointThunk.fulfilled,
                (
                    state,
                    {
                        payload: { result },
                        meta: {
                            arg: { pointId },
                        },
                    },
                ) => {
                    const hash = MD5(JSON.stringify(result)).toString();

                    state.pointsPriceLists[pointId] = hash;

                    const byBarcode = result.reduce<Record<string, Price>>(
                        (acc, item) => {
                            acc[item.barcode] = item;
                            return acc;
                        },
                        {},
                    );

                    state.priceLists[hash] = byBarcode;
                },
            )
            .addCase(getDebtListByPointIdThunk.fulfilled, (state, action) => {
                const { pointId } = action.meta.arg;

                state.debts[pointId] = arrayToRecord(
                    action.payload.result,
                    "consignmentId",
                );
            });

        builder.addMatcher(
            isAnyOf(
                getWorkGetScheduleThunk.fulfilled,
                getWorkStartAndGetScheduleThunk.fulfilled,
            ),
            (state, { payload }) => {
                const {
                    pointsPriceList,
                    pricelists,
                    pointsDebtList,
                    pointsContractList,
                    pointsInventories,
                    pointsPromoActual,
                    storageInventories,
                    promos,
                } = payload.result;

                state.pointsPriceLists = pointsPriceList ?? {};

                state.priceLists = Object.entries(pricelists ?? {}).reduce(
                    (acc, [key, list]) => {
                        acc[key] = Object.values(list).reduce<
                            Record<string, Price>
                        >((inner, pl) => {
                            inner[pl.barcode] = pl;
                            return inner;
                        }, {});
                        return acc;
                    },
                    {} as Record<string, Record<string, Price>>,
                );

                Object.entries(pointsDebtList ?? {})?.forEach(
                    ([key, value]) => {
                        state.debts[key] = arrayToRecord(
                            value,
                            "consignmentId",
                        );
                    },
                );

                state.targetsContracts = pointsContractList;
                state.pointsInventories =
                    normalizeNestedObjectMap(pointsInventories);
                state.promoList = promos;
                state.promosVocabulary = pointsPromoActual;
                state.inventoryList =
                    normalizeNestedObjectMap(storageInventories);
            },
        );
    },
});

export const {
    updateOfflineStatus,
    setShowOnlinePopup,
    addIgnoreRemainingOrder,
    removeIgnoreRemainingOrders,
    updateManualOfflineStatus,
    setLatency,
    setLastOfflineUpdate,
    setIsInitialDataLoading,
    setShouldUpdateKpi,
    setUpdateUnscheduledPointsProcessing,
    setPointInventories,
    deductInventory,
    setFilteredParams,

    // ------ RequestQueue
    addRequestQueueItem,
    bulkAddRequestQueueItems,
    deleteRequestQueueItem,
    patchRequestQueueItem,
    applyRequestQueueItemReplacements,
    addOrReplaceRequestQueueItem,
    upsertSaleOrderUpdateRequestQueueItem,
    restartRequestQueue,
    stopRequestQueue,
    normalizeRequestQueueTail,
    clearRequestQueue,
    setRequestQueueRunning,
    touchRequestQueue,

    // ------ Photo upload retry
    setNextPhotoUploadRetryAt,
    setPhotoUploadRetryProgress,
    setPhotoUploadRetryCandidatesCount,

    // ------ Store sync
    setProgressLastModifiedAt,
    setProgressLastSyncedAt,

    // ------ Reminder
    setCheckoutReminder,
    setTaskReminder,
} = offlineSlice.actions;

export default offlineSlice.reducer;
